import React, { useMemo } from 'react';

import { PinCode, PinCodePattern } from '@eika-kortbetjening/pin-code';
import { StateHandlingModal, StateHandlingModalProps } from '@eika-kortbetjening/state-handling-modal';

import { CreditCardService } from '@/domain/card';
import piwik from '@/piwik/piwik';
import { useCardPin } from '@/react-query/hooks/query/use-card-pin';

import { CouldNotFetchPINErrorCard, PINNotReadyErrorCard } from '@components/error/ErrorCard';
import { Card, CreditCard, OrderCardStatus } from '@generated-types';
import { useIsHighFraudRisk } from '@hooks/use-is-high-risk';
import useText from '@hooks/use-text';
import { isCardEnabledNets } from '@utils/card-utils';
import DEFAULT_APP_NAMESPACE from '@utils/modal';
import isValidPin from '@utils/pin-utils';

import './CardPinModal.scss';

interface CardPinModalModalProps extends StateHandlingModalProps {
    card: Card | CreditCard;
    open: boolean;
    setOpen: () => void;
}

const CardPinModal = ({ card, open, setOpen }: CardPinModalModalProps) => {
    const text = useText();
    const isHighFraudRisk = useIsHighFraudRisk();

    const enabled = !isHighFraudRisk && open;

    const { pinViewTitle, loadingPIN } = text;
    const {
        data: pin,
        isError: failedFetchingPin,
        error,
        isLoading: isFetchingPin,
        isSuccess: hasFetchedPin,
    } = useCardPin(card, { enabled });

    const PIN_COUNTDOWN_MS = 60000;

    const errorDisplay = useMemo(() => {
        if (failedFetchingPin && error) {
            const isV4 = CreditCardService.isCreditCard(card);
            if (!isV4 && (!isCardEnabledNets(card, text) || card.orderStatus === OrderCardStatus.PRODUCED)) {
                return <PINNotReadyErrorCard />;
            }
            return <CouldNotFetchPINErrorCard />;
        }

        return undefined;
    }, [failedFetchingPin, error, card, text]);

    return (
        <StateHandlingModal
            appNamespace={DEFAULT_APP_NAMESPACE}
            setOpen={setOpen}
            onClose={setOpen}
            id="pin-code"
            className="pin-modal"
            open={open}
            countdown={hasFetchedPin ? PIN_COUNTDOWN_MS : undefined}
            title={pinViewTitle}
            shouldCloseOnOverlayClick
            centerContentHorizontally
            centerContentVertically
            loading={isFetchingPin}
            loadingText={loadingPIN}
            error={errorDisplay}
        >
            {pin === null || !isValidPin(pin as PinCodePattern) ? null : (
                <PinCode pin={pin as PinCodePattern} piwik={piwik} />
            )}
        </StateHandlingModal>
    );
};

export default CardPinModal;
